import React from 'react';

// Performance monitoring utilities
export class PerformanceMonitor {
  private static metrics: Map<string, number> = new Map();
  private static observers: Map<string, PerformanceObserver> = new Map();

  // Core Web Vitals monitoring
  static initializeWebVitals() {
    // Largest Contentful Paint (LCP)
    this.observeMetric('LCP', (entries) => {
      const lastEntry = entries[entries.length - 1];
      this.metrics.set('LCP', lastEntry.startTime);
      console.log(`LCP: ${lastEntry.startTime}ms`);
    });

    // First Input Delay (FID)
    this.observeMetric('FID', (entries) => {
      entries.forEach((entry) => {
        const fid = entry.processingStart - entry.startTime;
        this.metrics.set('FID', fid);
        console.log(`FID: ${fid}ms`);
      });
    });

    // Cumulative Layout Shift (CLS)
    this.observeMetric('CLS', (entries) => {
      let clsValue = 0;
      entries.forEach((entry) => {
        if (!entry.hadRecentInput) {
          clsValue += entry.value;
        }
      });
      this.metrics.set('CLS', clsValue);
      console.log(`CLS: ${clsValue}`);
    });
  }

  private static observeMetric(type: string, callback: (entries: any[]) => void) {
    try {
      const observer = new PerformanceObserver((list) => {
        callback(list.getEntries());
      });
      
      observer.observe({ type: type.toLowerCase(), buffered: true });
      this.observers.set(type, observer);
    } catch (error) {
      console.warn(`Could not observe ${type}:`, error);
    }
  }

  // Bundle size analysis
  static analyzeBundleSize() {
    if ('performance' in window && 'getEntriesByType' in performance) {
      const resources = performance.getEntriesByType('resource') as PerformanceResourceTiming[];
      const jsResources = resources.filter(r => r.name.includes('.js'));
      const cssResources = resources.filter(r => r.name.includes('.css'));

      const totalJSSize = jsResources.reduce((sum, r) => sum + (r.transferSize || 0), 0);
      const totalCSSSize = cssResources.reduce((sum, r) => sum + (r.transferSize || 0), 0);

      console.group('📦 Bundle Analysis');
      console.log(`JavaScript: ${(totalJSSize / 1024).toFixed(2)} KB`);
      console.log(`CSS: ${(totalCSSSize / 1024).toFixed(2)} KB`);
      console.log(`Total: ${((totalJSSize + totalCSSSize) / 1024).toFixed(2)} KB`);
      console.groupEnd();

      return { js: totalJSSize, css: totalCSSSize, total: totalJSSize + totalCSSSize };
    }
    return null;
  }

  // Memory usage tracking
  static getMemoryUsage() {
    if ('memory' in performance) {
      const memory = (performance as any).memory;
      return {
        used: Math.round(memory.usedJSHeapSize / 1024 / 1024),
        total: Math.round(memory.totalJSHeapSize / 1024 / 1024),
        limit: Math.round(memory.jsHeapSizeLimit / 1024 / 1024)
      };
    }
    return null;
  }

  // Component render tracking
  static trackComponentRender(componentName: string) {
    const startTime = performance.now();
    return () => {
      const endTime = performance.now();
      const renderTime = endTime - startTime;
      console.log(`🎨 ${componentName} rendered in ${renderTime.toFixed(2)}ms`);
      return renderTime;
    };
  }

  // HOC for performance monitoring
  static withPerformanceTracking<P extends object>(
    Component: React.ComponentType<P>,
    componentName?: string
  ) {
    const name = componentName || Component.displayName || Component.name || 'Unknown';
    
    return React.memo((props: P) => {
      const trackRender = React.useCallback(() => {
        return this.trackComponentRender(name);
      }, []);

      React.useEffect(() => {
        const endTracking = trackRender();
        return endTracking;
      });

      return <Component {...props} />;
    });
  };

  // Performance report
  static generateReport() {
    const report = {
      timestamp: new Date().toISOString(),
      metrics: Object.fromEntries(this.metrics),
      memory: this.getMemoryUsage(),
      bundle: this.analyzeBundleSize(),
      navigation: performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming
    };

    console.group('📊 Performance Report');
    console.table(report.metrics);
    if (report.memory) {
      console.log('Memory:', report.memory);
    }
    if (report.bundle) {
      console.log('Bundle:', report.bundle);
    }
    console.groupEnd();

    return report;
  }

  // Cleanup
  static cleanup() {
    this.observers.forEach(observer => observer.disconnect());
    this.observers.clear();
    this.metrics.clear();
  }
}

// Performance monitoring hook
export const usePerformanceMonitoring = (componentName: string) => {
  React.useEffect(() => {
    const endTracking = PerformanceMonitor.trackComponentRender(componentName);
    return endTracking;
  }, [componentName]);
};

// Performance context
export const PerformanceContext = React.createContext<{
  metrics: Map<string, number>;
  generateReport: () => any;
}>({
  metrics: new Map(),
  generateReport: () => ({})
});

// Performance provider
export const PerformanceProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  React.useEffect(() => {
    PerformanceMonitor.initializeWebVitals();
    return () => PerformanceMonitor.cleanup();
  }, []);

  const value = React.useMemo(() => ({
    metrics: PerformanceMonitor['metrics'],
    generateReport: PerformanceMonitor.generateReport
  }), []);

  return (
    <PerformanceContext.Provider value={value}>
      {children}
    </PerformanceContext.Provider>
  );
};